import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { clampBillingPeriod, useBillingHistoryAccess } from '../utils/billingAccess';

/**
 * The financial reporting window shared by every money page.
 *
 * Billing, the Top Sheet and the dashboard all read the same consolidated
 * Top Sheet service. Keeping the window in one place (rather than each page
 * owning its own date state) is what makes the numbers agree: change it once
 * and every page re-reads the same range under the same React Query key.
 *
 * Dates are plain `YYYY-MM-DD` strings. An empty string means "unbounded",
 * and both empty means "All time".
 */
export interface FinancialPeriod {
  /** Inclusive start date (`YYYY-MM-DD`), or '' for unbounded. */
  from: string;
  /** Inclusive end date (`YYYY-MM-DD`), or '' for unbounded. */
  to: string;
}

/** An unbounded reporting window: everything, from the beginning of records. */
export const ALL_TIME_PERIOD: FinancialPeriod = { from: '', to: '' };

/**
 * The reporting window a money page opens on when nothing has been chosen yet:
 * today only, so the overview shows the current day's position rather than an
 * all-time total. "All Time" stays available as an explicit choice.
 */
export function defaultFinancialPeriod(today: Date = new Date()): FinancialPeriod {
  const day = toDateInput(today);
  return { from: day, to: day };
}

interface FinancialPeriodContextValue {
  period: FinancialPeriod;
  setPeriod: (period: FinancialPeriod) => void;
}

// Versioned so the switch to a "today" default is picked up even for browsers
// that still hold the old all-time window under the previous key.
const STORAGE_KEY = 'firstaid:financial-period:v2';

const FinancialPeriodContext = createContext<FinancialPeriodContextValue | null>(null);

function readStoredPeriod(): FinancialPeriod {
  if (typeof window === 'undefined') return defaultFinancialPeriod();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultFinancialPeriod();
    const parsed = JSON.parse(raw) as Partial<FinancialPeriod>;
    return { from: parsed.from ?? '', to: parsed.to ?? '' };
  } catch {
    return defaultFinancialPeriod();
  }
}

/**
 * Holds the shared financial period for the whole app and persists it so the
 * choice survives navigation and reloads.
 */
export function FinancialPeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<FinancialPeriod>(readStoredPeriod);
  const { unrestricted } = useBillingHistoryAccess();

  const setPeriod = useCallback(
    (next: FinancialPeriod) => {
      // Restricted roles can never persist an out-of-window selection.
      const safe = unrestricted ? next : clampBillingPeriod(next);
      setPeriodState(safe);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
      } catch {
        // Persistence is best-effort; the in-memory value still applies.
      }
    },
    [unrestricted],
  );

  // A period persisted by an admin earlier in this browser may be older than
  // seven days; clamp what is exposed so every money page reads a legal range.
  const visiblePeriod = useMemo(
    () => (unrestricted ? period : clampBillingPeriod(period)),
    [period, unrestricted],
  );

  const value = useMemo(
    () => ({ period: visiblePeriod, setPeriod }),
    [visiblePeriod, setPeriod],
  );
  return (
    <FinancialPeriodContext.Provider value={value}>{children}</FinancialPeriodContext.Provider>
  );
}

/** Reads and updates the shared financial period. */
export function useFinancialPeriod(): FinancialPeriodContextValue {
  const ctx = useContext(FinancialPeriodContext);
  if (!ctx) {
    throw new Error('useFinancialPeriod must be used within a FinancialPeriodProvider');
  }
  return ctx;
}

/** Formats a Date as a local `YYYY-MM-DD` (never shifts the day via UTC). */
export function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Short human label for a period, e.g. "All time" or "1/10/2026 – 14/10/2026". */
export function describeFinancialPeriod(period: FinancialPeriod): string {
  if (!period.from && !period.to) return 'All time';
  const fmt = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };
  if (period.from && period.to) return `${fmt(period.from)} — ${fmt(period.to)}`;
  if (period.from) return `From ${fmt(period.from)}`;
  return `Until ${fmt(period.to)}`;
}
